// :)
/**
 * Atomically merge Synkro hook entries into ~/.claude/settings.json.
 *
 * Preserves any other hooks the user has configured (Corridor, Noma, custom
 * scripts, etc.) — we only add our entries, never replace the file.
 */
import { existsSync, readFileSync, writeFileSync, renameSync, mkdirSync, unlinkSync } from 'node:fs';
import { dirname } from 'node:path';

export interface SynkroHookConfig {
  bashJudgeScriptPath: string;
  bashFollowupScriptPath: string;
  editPrecheckScriptPath: string;
  cwePrecheckScriptPath: string;
  cvePrecheckScriptPath: string;
  planJudgeScriptPath: string;
  agentJudgeScriptPath: string;
  stopSummaryScriptPath: string;
  sessionStartScriptPath: string;
  transcriptSyncScriptPath: string;
  userPromptSubmitScriptPath: string;
  installScanScriptPath: string;
  taskActivateIntentScriptPath?: string;
  mcpGateScriptPath?: string;
  skipTranscriptSync?: boolean;
}

const SYNKRO_MARKER = '__synkro_managed__';

interface HookEntry {
  matcher?: string;
  hooks: Array<Record<string, unknown>>;
  [SYNKRO_MARKER]?: boolean;
}

interface CCSettings {
  hooks?: {
    PreToolUse?: HookEntry[];
    PostToolUse?: HookEntry[];
    SessionEnd?: HookEntry[];
    SessionStart?: HookEntry[];
    Stop?: HookEntry[];
    [k: string]: unknown;
  };
  [k: string]: unknown;
}

function readSettings(path: string): CCSettings {
  if (!existsSync(path)) return {};
  try {
    const raw = readFileSync(path, 'utf-8');
    return JSON.parse(raw) as CCSettings;
  } catch (err) {
    throw new Error(`Failed to parse ${path}: ${(err as Error).message}`);
  }
}

function writeSettingsAtomic(path: string, settings: CCSettings): void {
  mkdirSync(dirname(path), { recursive: true });
  const tmpPath = `${path}.synkro.tmp`;
  writeFileSync(tmpPath, JSON.stringify(settings, null, 2) + '\n', 'utf-8');
  renameSync(tmpPath, path);
}

function isSynkroEntry(entry: any): boolean {
  if (entry?.[SYNKRO_MARKER]) return true;
  const hooks = Array.isArray(entry?.hooks) ? entry.hooks : [];
  return hooks.some((h: any) =>
    typeof h?.command === 'string' && h.command.includes('/.synkro/hooks/'),
  );
}

function removeSynkroEntries(events: CCSettings['hooks'] extends infer H ? H : never, eventName: string): void {
  if (!events) return;
  const arr = (events as any)[eventName];
  if (!Array.isArray(arr)) return;
  (events as any)[eventName] = arr.filter((entry: any) => !isSynkroEntry(entry));
}

/**
 * Merge Synkro hooks into the settings file. Idempotent — replaces any
 * existing Synkro-managed entries with the new versions.
 */
export function installCCHooks(settingsPath: string, config: SynkroHookConfig): void {
  const settings = readSettings(settingsPath);
  settings.hooks = settings.hooks ?? {};

  // Remove any prior Synkro entries (to support `synkro update`)
  removeSynkroEntries(settings.hooks as any, 'PreToolUse');
  removeSynkroEntries(settings.hooks as any, 'PostToolUse');
  removeSynkroEntries(settings.hooks as any, 'SessionEnd');
  removeSynkroEntries(settings.hooks as any, 'SessionStart');
  removeSynkroEntries(settings.hooks as any, 'UserPromptSubmit');
  // Also clean up any older `Stop`-event entry from earlier v1.6 builds.
  removeSynkroEntries(settings.hooks as any, 'Stop');

  settings.hooks.PreToolUse = settings.hooks.PreToolUse ?? [];
  settings.hooks.PostToolUse = settings.hooks.PostToolUse ?? [];
  settings.hooks.SessionEnd = settings.hooks.SessionEnd ?? [];
  settings.hooks.SessionStart = settings.hooks.SessionStart ?? [];
  settings.hooks.UserPromptSubmit = (settings.hooks.UserPromptSubmit as any[]) ?? [];

  // PreToolUse Bash → install scan (caches result for bash judge to read)
  settings.hooks.PreToolUse.push({
    matcher: 'Bash',
    hooks: [
      {
        type: 'command',
        command: config.installScanScriptPath,
        timeout: 8,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  // PreToolUse Bash/Read/Grep/Glob → command hook script (Cerebras-judged)
  settings.hooks.PreToolUse.push({
    matcher: 'Bash|Read|Grep|Glob',
    hooks: [
      {
        type: 'command',
        command: config.bashJudgeScriptPath,
        timeout: 50,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  // PreToolUse Edit/Write → two hooks in ONE entry so CC waits for all
  // before processing deny decisions. Each hook runs in parallel:
  //   1. edit-precheck: org rules grading on channel 1 (port 8929)
  //   2. cve-precheck:  CVE/OSV dependency scan (curl, no LLM)
  settings.hooks.PreToolUse.push({
    matcher: 'Edit|Write|MultiEdit|NotebookEdit',
    hooks: [
      {
        type: 'command',
        command: config.editPrecheckScriptPath,
        timeout: 50,
      },
      {
        type: 'command',
        command: config.cwePrecheckScriptPath,
        timeout: 50,
      },
      {
        type: 'command',
        command: config.cvePrecheckScriptPath,
        timeout: 10,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  // PreToolUse Agent → scan subagent prompts against org rules.
  settings.hooks.PreToolUse.push({
    matcher: 'Agent',
    hooks: [
      {
        type: 'command',
        command: config.agentJudgeScriptPath,
        timeout: 50,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  // PreToolUse ExitPlanMode → advisory plan review against org rules.
  settings.hooks.PreToolUse.push({
    matcher: 'ExitPlanMode',
    hooks: [
      {
        type: 'command',
        command: config.planJudgeScriptPath,
        timeout: 50,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  // PreToolUse on any mcp__* tool → MCP server governance (allow/blocklist +
  // grade allowed calls). Blocking: a blocklisted/un-allowlisted server is denied.
  // Synkro's own guardrails server is skipped inside the handler, never blocked.
  if (config.mcpGateScriptPath) {
    settings.hooks.PreToolUse.push({
      matcher: 'mcp__.*',
      hooks: [
        {
          type: 'command',
          command: config.mcpGateScriptPath,
          timeout: 50,
        },
      ],
      [SYNKRO_MARKER]: true,
    } as any);
  }

  // PreToolUse on the activate_standard MCP tool → stamp the activating session
  // BEFORE the tool runs, so the task binds to THIS exact session (reliable —
  // not the most-recent-session heuristic). Matcher is a literal MCP tool name.
  if (config.taskActivateIntentScriptPath) {
    settings.hooks.PreToolUse.push({
      matcher: 'mcp__synkro-guardrails__activate_standard',
      hooks: [
        {
          type: 'command',
          command: config.taskActivateIntentScriptPath,
          timeout: 5,
        },
      ],
      [SYNKRO_MARKER]: true,
    } as any);
  }

  // PostToolUse Edit/Write removed — PreToolUse hooks handle grading now.

  // PostToolUse Bash → flips pending precheck_corrections row to 'allow'
  // once the bash command actually executed. Required for the bash trendline
  // (approved-vs-rejected) the dashboard reads from precheck_corrections.
  settings.hooks.PostToolUse.push({
    matcher: 'Bash',
    hooks: [
      {
        type: 'command',
        command: config.bashFollowupScriptPath,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  // SessionEnd → end-of-session summary line (`[synkro] stop → N findings: ...`).
  // We use SessionEnd, not Stop, because Stop fires after every agent turn
  // (which would spam the user in interactive mode); SessionEnd fires once
  // when the session itself terminates.
  settings.hooks.SessionEnd.push({
    hooks: [
      {
        type: 'command',
        command: config.stopSummaryScriptPath,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  // SessionStart → "[synkro] session start → N open findings in this repo" if any.
  settings.hooks.SessionStart.push({
    hooks: [
      {
        type: 'command',
        command: config.sessionStartScriptPath,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  // UserPromptSubmit → captures explicit consent keywords from user messages.
  // Writes a file-based grant so the next blocked tool call is allowed through.
  (settings.hooks.UserPromptSubmit as any[]).push({
    hooks: [
      {
        type: 'command',
        command: config.userPromptSubmitScriptPath,
        timeout: 5,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  // Stop → usage telemetry + optional transcript sync (fires after every agent turn).
  // Always installed: usage tracking is ungated; transcript sync is gated inside the script.
  settings.hooks.Stop = settings.hooks.Stop ?? [];
  removeSynkroEntries(settings.hooks as any, 'Stop');
  settings.hooks.Stop.push({
    hooks: [
      {
        type: 'command',
        command: config.transcriptSyncScriptPath,
        timeout: 3,
      },
    ],
    [SYNKRO_MARKER]: true,
  } as any);

  writeSettingsAtomic(settingsPath, settings);
}

/**
 * Remove all Synkro-managed hook entries from settings.json.
 * Used by `synkro disconnect`.
 */
export function uninstallCCHooks(settingsPath: string): boolean {
  if (!existsSync(settingsPath)) return false;
  const settings = readSettings(settingsPath);
  if (!settings.hooks) return false;

  const events = ['PreToolUse', 'PostToolUse', 'SessionEnd', 'SessionStart', 'Stop', 'UserPromptSubmit'] as const;
  for (const evt of events) {
    removeSynkroEntries(settings.hooks as any, evt);
  }

  // If a hook event array is now empty, delete it
  for (const evt of events) {
    if (Array.isArray((settings.hooks as any)[evt]) && (settings.hooks as any)[evt].length === 0) {
      delete (settings.hooks as any)[evt];
    }
  }
  // If hooks object is now empty, delete it
  if (Object.keys(settings.hooks).length === 0) {
    delete settings.hooks;
  }

  writeSettingsAtomic(settingsPath, settings);
  return true;
}

/**
 * Check whether Synkro hooks are currently installed in settings.json.
 * Used by `synkro status`.
 */
export function inspectCCHooks(settingsPath: string): {
  installed: boolean;
  preToolUseBash: boolean;
  postToolUseEdit: boolean;
  sessionEnd: boolean;
  sessionStart: boolean;
} {
  if (!existsSync(settingsPath)) {
    return { installed: false, preToolUseBash: false, postToolUseEdit: false, sessionEnd: false, sessionStart: false };
  }
  const settings = readSettings(settingsPath);
  const pre = (settings.hooks as any)?.PreToolUse ?? [];
  const post = (settings.hooks as any)?.PostToolUse ?? [];
  const sessionEndHooks = (settings.hooks as any)?.SessionEnd ?? [];
  const sessionStartHooks = (settings.hooks as any)?.SessionStart ?? [];
  const preToolUseBash = pre.some((e: any) => e?.[SYNKRO_MARKER] === true);
  const postToolUseEdit = post.some((e: any) => e?.[SYNKRO_MARKER] === true);
  const sessionEnd = sessionEndHooks.some((e: any) => e?.[SYNKRO_MARKER] === true);
  const sessionStart = sessionStartHooks.some((e: any) => e?.[SYNKRO_MARKER] === true);
  return {
    installed: preToolUseBash || postToolUseEdit || sessionEnd || sessionStart,
    preToolUseBash,
    postToolUseEdit,
    sessionEnd,
    sessionStart,
  };
}
